import { resolvePreviousStepTypeFromHistory } from "@/services/extrasellFunnel";

const ORDER_TYPE_TO_LAMBDA_STEP_TYPE = {
  initial: "initial",
  upsell: "postUpsells",
  crosssell: "crossSells",
};

const CONSUMED_EXTRASELL_STEP_TYPES = new Set([
  "upsell",
  "postUpsells",
  "crossSells",
]);

/**
 * Lambda /extrasell expects postUpsells / crossSells. History items store
 * that value on skip/order via _currentStepType; older entries only have
 * order.orderType (initial / upsell / crosssell).
 */
export function resolveHistoryStepType(historyItem) {
  if (historyItem?.stepType) {
    return historyItem.stepType;
  }
  const orderType = historyItem?.order?.orderType;
  return ORDER_TYPE_TO_LAMBDA_STEP_TYPE[orderType] || orderType || null;
}

export function isAcceptedExtrasellHistoryItem(historyItem) {
  return isConsumedExtrasellHistoryItem(historyItem, { actions: ["order"] });
}

export function isConsumedExtrasellHistoryItem(
  historyItem,
  { actions = ["order", "skip"] } = {},
) {
  if (!historyItem || !actions.includes(historyItem.action)) {
    return false;
  }
  return CONSUMED_EXTRASELL_STEP_TYPES.has(resolveHistoryStepType(historyItem));
}

export function historyHasConsumedPostUpsell(history) {
  return (Array.isArray(history) ? history : []).some((item) => {
    const stepType = resolveHistoryStepType(item);
    return (
      (stepType === "upsell" || stepType === "postUpsells") &&
      isConsumedExtrasellHistoryItem(item)
    );
  });
}

export function historyHasConsumedExtrasell(history) {
  return (Array.isArray(history) ? history : []).some((item) =>
    isConsumedExtrasellHistoryItem(item),
  );
}

function previewExtrasellConsumedKey(domainId) {
  return `preview_extrasell_consumed_${domainId || "default"}`;
}

export function markPreviewExtrasellConsumed(domainId) {
  try {
    sessionStorage.setItem(previewExtrasellConsumedKey(domainId), "1");
  } catch {
    /* storage unavailable */
  }
}

export function clearPreviewExtrasellConsumed(domainId) {
  try {
    sessionStorage.removeItem(previewExtrasellConsumedKey(domainId));
  } catch {
    /* storage unavailable */
  }
}

export function isPreviewExtrasellConsumed(domainId) {
  try {
    return (
      sessionStorage.getItem(previewExtrasellConsumedKey(domainId)) === "1"
    );
  } catch {
    return false;
  }
}

export function isStalePostUpsellPayload(
  data,
  lastHistoryItem,
  history = [],
  { previewConsumed = false } = {},
) {
  if (data?.stepType !== "postUpsells") {
    return false;
  }
  // The upsell of a cross-sell is the next step after that cross-sell is bought.
  if (
    resolveHistoryStepType(lastHistoryItem) === "crossSells" &&
    lastHistoryItem?.action === "order"
  ) {
    return false;
  }
  if (isConsumedExtrasellHistoryItem(lastHistoryItem)) {
    return true;
  }
  const lastStepType = resolveHistoryStepType(lastHistoryItem);
  if (!lastStepType || lastStepType === "initial") {
    return false;
  }
  return previewConsumed || historyHasConsumedExtrasell(history);
}

export function resolveUpsellParentChoiceIds(history) {
  const items = Array.isArray(history) ? history : [];
  const last = items[items.length - 1];
  if (
    resolveHistoryStepType(last) === "crossSells" &&
    last?.action === "order"
  ) {
    return (last.cart?.items || [])
      .map((cartItem) => cartItem.choice?.id)
      .filter(Boolean);
  }
  if (isConsumedExtrasellHistoryItem(last)) {
    return [];
  }

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (CONSUMED_EXTRASELL_STEP_TYPES.has(resolveHistoryStepType(item))) {
      continue;
    }
    return (item.cart?.items || []).map((cartItem) => cartItem.choice?.id);
  }

  return [];
}

export function buildExtrasellHistory(historyFromStorage) {
  if (!Array.isArray(historyFromStorage)) {
    return [];
  }

  return historyFromStorage.map((historyItem) => {
    const entry = {
      step: historyItem.step,
      stepType: resolveHistoryStepType(historyItem),
      choices: [],
    };

    if (historyItem.action) {
      entry.action = historyItem.action;
    }

    for (const cartItem of historyItem.cart?.items || []) {
      entry.choices.push({
        productDetailsId: cartItem.id,
        qty: cartItem.qty,
        choiceId: cartItem.choice.id,
      });
    }

    return entry;
  });
}

export function buildExtrasellPayload({
  historyFromStorage,
  domainId,
  fallbackStepType = null,
}) {
  const history = buildExtrasellHistory(historyFromStorage);
  const currentStep = history.length > 0 ? history[history.length - 1].step : 0;

  return {
    domainId,
    history,
    currentStep,
    previousStepType: resolvePreviousStepTypeFromHistory(
      history,
      fallbackStepType,
    ),
  };
}
